import * as Comp from './popover-content.styles'
import { useWindowDimensions, View } from 'react-native'
import { PopoverHeader } from '../popover-header/index.tsx'
import { PopoverArrow } from '../popover-arrow/index.tsx'
import { Button } from '~/components/button'
import { useState } from 'react'
import { layoutUtils } from '~/utils/layout/layout-utils'
import type { LayoutChangeEvent, ViewProps } from 'react-native'
import {
  useTheme,
  type LayoutInfo,
  useFloatingPosition,
} from '~/hooks/index.ts'
import {
  PositionAlignment,
  PositionDirection,
} from '~/utils/layout/position-enums'

interface PopoverContentProps extends ViewProps {
  isOpen?: boolean
  title?: string
  description?: string
  onClose?: () => void
  side?: PositionDirection
  align?: PositionAlignment
  autoPosition?: boolean
  rootLayout?: LayoutInfo
  tag?: React.ReactNode
  rootLayoutReady?: boolean
  buttonText?: string
  onButtonPress?: () => void
}

export function PopoverContent({
  isOpen = false,
  title = '',
  description = '',
  onClose = () => {},
  side = PositionDirection.TOP,
  align = PositionAlignment.MIDDLE,
  autoPosition = true,
  rootLayout,
  tag,
  rootLayoutReady = false,
  buttonText,
  onButtonPress,
  ...viewProps
}: PopoverContentProps) {
  const { width: screenWidth } = useWindowDimensions()
  const theme = useTheme()

  const SCREEN_MARGIN = theme.spacingInlineFourpulse
  const CONTENT_GAP = theme.spacingInlineTwopulse
  const HALF = 2

  const [contentLayout, setContentLayout] = useState<LayoutInfo>()

  const { side: finalSide, styles: positionStyles } = useFloatingPosition({
    side,
    align,
    autoPosition,
    rootLayout: rootLayout || null,
    contentLayout: contentLayout || null,
    gap: CONTENT_GAP,
    screenPadding: SCREEN_MARGIN / HALF,
  })

  const handleLayout = (event: LayoutChangeEvent) => {
    const { x, y, width, height } = event.nativeEvent.layout
    const newLayout = { x, y, width, height }

    if (layoutUtils.hasLayoutChanged(contentLayout, newLayout)) {
      setContentLayout(newLayout)
    }
  }

  const isReady = rootLayoutReady && !!contentLayout

  if (!isOpen) return null

  return (
    <Comp.Wrapper
      style={[
        {
          width: screenWidth - SCREEN_MARGIN,
          alignItems: 'center',
          height: (rootLayout?.height || 0) + (contentLayout?.height || 0),
          left: -(rootLayout?.x || 0) + SCREEN_MARGIN / HALF,
          opacity: isReady ? 1 : 0,
        },
        positionStyles.wrapper,
      ]}
      pointerEvents="box-none"
    >
      <PopoverArrow side={finalSide} rootLayout={rootLayout} />
      <Comp.Container
        style={{
          ...positionStyles.container,
        }}
        onLayout={handleLayout}
        {...viewProps}
      >
        <PopoverHeader
          title={title}
          tag={tag}
          onClose={onClose}
          testID="popover-content-header"
        />
        {description && (
          <Comp.Description numberOfLines={3}>{description}</Comp.Description>
        )}
        {buttonText && onButtonPress && (
          <View
            style={{
              alignSelf: 'stretch',
            }}
          >
            <Button.Root
              testID="popover-content-button"
              onPress={onButtonPress}
              variant="neutral-secondary"
              size="ml"
              full
              style={{
                marginTop: theme.spacingInlineTwopulse,
              }}
            >
              <Button.Label>{buttonText}</Button.Label>
            </Button.Root>
          </View>
        )}
      </Comp.Container>
    </Comp.Wrapper>
  )
}
